import type { FetchUserAttributesOutput } from 'aws-amplify/auth';
import { captureExceptionWithRecording } from '@whitelabel/helpers/utils';
import { ICustomerRegion } from '@whitelabel/helpers/types';
import { getLocalStorage } from '@whitelabel/helpers/storageUtils';
import { IError } from './types';
import { API_HOST } from './api';
import {
  AP_SOUTH_1,
  EU_CENTRAL_1,
  CUSTOMER_REGION,
  REGIONS,
  US_EAST_1,
  SIGNOUT_CALLBACK_PATH,
  SIGNIN_CALLBACK_PATH,
} from './constants';

// This is from:
// https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest#converting_a_digest_to_a_hex_string
export async function hash(text: string) {
  const msgUint8 = new TextEncoder().encode(text); // encode as (utf-8) Uint8Array
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8); // hash the message
  const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join(''); // convert bytes to hex string
  return hashHex;
}

export async function fetchAndStoreCustomerRegion(
  emailOrPhone: string,
  baseUrl: string | undefined = API_HOST,
): Promise<ICustomerRegion> {
  try {
    const hashedEmailOrPhone = await hash(emailOrPhone.trim().toLocaleLowerCase());
    const response = await fetch(`${baseUrl}/v1/customers/region/${hashedEmailOrPhone}/`);
    let data = (await response.json()) as ICustomerRegion | IError;

    if (response.ok) {
      data = data as ICustomerRegion;
      localStorage.setItem(CUSTOMER_REGION, data.region);
    } else {
      const apiError = data as IError;
      const requestId = response.headers.get('x-request-id');
      const error: IError = new Error(apiError.message);
      error.requestId = requestId;
      error.status = response.status;
      error.code = apiError.errors?.code;
      throw error;
    }
    return data as ICustomerRegion;
  } catch (e) {
    if ((e as IError).status !== 404) {
      captureExceptionWithRecording(e as IError, {
        emailOrPhone,
        error: JSON.stringify(e),
        url: `/api/v1/customers/region/`,
      });
    }
    throw e;
  }
}

interface IRegion {
  id: string;
  region: string;
}

export const REGION_ID_COGNITO_TOKEN_FIELD_MAPPINGS = {
  'us-east-1': 'custom:cid_usea1',
  'eu-central-1': 'custom:cid_euce1',
  'ap-south-1': 'custom:cid_apso1',
};

export function storeRegions(mainCustomerID: string, attributes?: FetchUserAttributesOutput) {
  const regions: IRegion[] = [];
  Object.entries(REGION_ID_COGNITO_TOKEN_FIELD_MAPPINGS).forEach(([region, field]) => {
    const customerID = attributes?.[field];
    if (customerID) {
      if (customerID === mainCustomerID) {
        regions.unshift({ id: customerID, region });
      } else {
        regions.push({ id: customerID, region });
      }
    }
  });
  if (regions.length > 0) {
    localStorage.setItem(REGIONS, JSON.stringify(regions));
  }
}

export function getStoredCustomerRegion() {
  return getLocalStorage(CUSTOMER_REGION);
}
export function getStoredRegions() {
  return getLocalStorage<IRegion[]>(REGIONS, true);
}

export async function configureCognito() {
  const region = getStoredCustomerRegion();
  if (region) {
    let cognitoRegion = process.env.REACT_APP_COGNITO_REGION || process.env.NEXT_PUBLIC_COGNITO_REGION;
    let cognitoUserPoolID = process.env.REACT_APP_COGNITO_USER_POOL_ID || process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID;
    let cognitoAppClientID =
      process.env.REACT_APP_COGNITO_APP_CLIENT_ID || process.env.NEXT_PUBLIC_COGNITO_APP_CLIENT_ID;
    const authDomainPrefix =
      process.env.REACT_APP_COGNITO_AUTH_DOMAIN_PREFIX || process.env.NEXT_PUBLIC_COGNITO_AUTH_DOMAIN_PREFIX;

    if (region === AP_SOUTH_1) {
      cognitoRegion =
        process.env.REACT_APP_COGNITO_AP_SOUTH_1_REGION || process.env.NEXT_PUBLIC_COGNITO_AP_SOUTH_1_REGION;
      cognitoUserPoolID =
        process.env.REACT_APP_COGNITO_AP_SOUTH_1_USER_POOL_ID ||
        process.env.NEXT_PUBLIC_COGNITO_AP_SOUTH_1_USER_POOL_ID;
      cognitoAppClientID =
        process.env.REACT_APP_COGNITO_AP_SOUTH_1_APP_CLIENT_ID ||
        process.env.NEXT_PUBLIC_COGNITO_AP_SOUTH_1_APP_CLIENT_ID;
    } else if (region === US_EAST_1) {
      cognitoRegion =
        process.env.REACT_APP_COGNITO_US_EAST_1_REGION || process.env.NEXT_PUBLIC_COGNITO_US_EAST_1_REGION;
      cognitoUserPoolID =
        process.env.REACT_APP_COGNITO_US_EAST_1_USER_POOL_ID || process.env.NEXT_PUBLIC_COGNITO_US_EAST_1_USER_POOL_ID;
      cognitoAppClientID =
        process.env.REACT_APP_COGNITO_US_EAST_1_APP_CLIENT_ID ||
        process.env.NEXT_PUBLIC_COGNITO_US_EAST_1_APP_CLIENT_ID;
    }

    const authDomain = `${authDomainPrefix}.${cognitoRegion}.amazoncognito.com`;

    if (cognitoUserPoolID && cognitoAppClientID) {
      try {
        // Dynamic import to reduce initial bundle size
        const { Amplify } = await import('aws-amplify');
        Amplify.configure({
          Auth: {
            Cognito: {
              userPoolId: cognitoUserPoolID,
              userPoolClientId: cognitoAppClientID,
              passwordFormat: {
                minLength: 8,
                requireLowercase: false,
                requireNumbers: true,
                requireUppercase: false,
                requireSpecialCharacters: false,
              },
              loginWith: {
                oauth: {
                  domain: authDomain,
                  redirectSignIn: [`${window.location.origin}${SIGNIN_CALLBACK_PATH}`],
                  redirectSignOut: [`${window.location.origin}${SIGNOUT_CALLBACK_PATH}`],
                  responseType: 'code', // or 'token', note that REFRESH token will only be generated when the responseType is code
                  scopes: ['profile', 'email', 'openid', 'aws.cognito.signin.user.admin'],
                },
              },
            },
          },
        });
      } catch (error) {
        captureExceptionWithRecording(error as Error, { cognitoAppClientID, cognitoUserPoolID, authDomain });
      }
    }
  }
}

export function getSelectedAPIHost(
  euCentral1APIHost: string,
  usEast1APIHost: string,
  apSouth1APIHost: string,
  id?: string | null,
  customerID?: string,
) {
  if (id) {
    if (id.startsWith('I-')) {
      return apSouth1APIHost;
    }
    if (id.startsWith('U-')) {
      return usEast1APIHost;
    }
    return euCentral1APIHost;
  }

  let region;

  if (customerID) {
    const storedRegions = getStoredRegions();
    if (storedRegions) {
      region = storedRegions.find((storedRegion) => storedRegion.id === customerID)?.region;
    }
  }

  if (!region) {
    region = getStoredCustomerRegion();
  }

  if (region === AP_SOUTH_1) {
    return apSouth1APIHost;
  }
  if (region === US_EAST_1) {
    return usEast1APIHost;
  }
  return euCentral1APIHost;
}

export function getAPIHost({
  id,
  version = 'v1',
  customerID,
}: {
  id?: string;
  version?: 'v1' | 'v2' | 'v3';
  customerID?: string;
} = {}) {
  const addVersion = (url: string) => `${url}/${version}`;

  const apSouth1APIHost = addVersion(
    (process.env.REACT_APP_AP_SOUTH_1_API_HOST || process.env.NEXT_PUBLIC_AP_SOUTH_1_API_HOST)!,
  );
  const usEast1APIHost = addVersion(
    (process.env.REACT_APP_US_EAST_1_API_HOST || process.env.NEXT_PUBLIC_US_EAST_1_API_HOST)!,
  );
  const euCentral1APIHost = addVersion(API_HOST || '/api');

  return getSelectedAPIHost(euCentral1APIHost, usEast1APIHost, apSouth1APIHost, id, customerID);
}

export function getNestAPIHost({
  id,
  version = 'v1',
  customerID,
}: {
  id?: string | null;
  version?: 'v1' | 'v2' | 'v3';
  customerID?: string;
} = {}) {
  const addVersion = (url: string) => `${url}/${version}`;

  const apSouth1APIHost = addVersion('/router/api-xc-ap_south_1');
  const usEast1APIHost = addVersion('/router/api-xc-us_east_1');
  const euCentral1APIHost = addVersion('/router/api-xc-eu_central_1');

  return getSelectedAPIHost(euCentral1APIHost, usEast1APIHost, apSouth1APIHost, id, customerID);
}

export function storeCustomerRegion(region: string | null) {
  /**
   * If region is available in the URL, force update the region.
   * Else check if customerRegion already exist in LS.
   ** If there's an existing LS, honor the existing customerRegion and no changes.
   ** Else default the customerRegion to EU_CENTRAL_1.
   */
  localStorage.setItem(CUSTOMER_REGION, region || localStorage.getItem(CUSTOMER_REGION) || EU_CENTRAL_1);
}

export function storeCustomerRegionFromURL(search?: string) {
  const urlParams = new URLSearchParams(search || window.location.search);
  const region = urlParams.get('region');
  storeCustomerRegion(region);
}

export function getRegion(ins: string) {
  if (ins.startsWith('I-')) {
    return AP_SOUTH_1;
  }
  if (ins.startsWith('U-')) {
    return US_EAST_1;
  }
  return EU_CENTRAL_1;
}
